import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { PATHS } from '../../../navigation/data';
import {
  computeDeliveryNoteToggleAction,
  DeliveryNoteToggleInput,
  DeliveryNoteToggleResult,
} from '../../../services/utils/deliveryNoteUtils';
import { canBeMarkedAsDeliveryNote, isDeliveryNoteDisabledByDefault } from '../../../services/utils/myDataUtils';
import { BToggle } from '../BToggle';

interface Props {
  field: any;
  formik: any;
}

export const DeliveryNoteToggle = ({ field, formik }: Props) => {
  const { pathname } = useLocation();
  const getFieldMeta = formik?.getFieldMeta;
  const setFieldValue = formik?.setFieldValue;
  const documentType = useMemo(
    () => formik?.values?.documentTypeId?.object?.aadeCode,
    [formik?.values?.documentTypeId],
  );
  const initialDocumentType = useMemo(
    () => formik?.initialValues?.documentTypeId?.object?.aadeCode,
    [formik?.initialValues?.documentTypeId],
  );
  const isEdit = useMemo(() => pathname.includes(PATHS.EDIT_SALE), [pathname]);
  // Treat clone-prefill as a special case that should recommend enabling
  const isClone = useMemo(() => pathname.includes(PATHS.CLONE_SALE), [pathname]);
  const isEnabled = useMemo(() => canBeMarkedAsDeliveryNote(documentType), [documentType]);
  const disabledByDefault = useMemo(() => isDeliveryNoteDisabledByDefault(documentType), [documentType]);

  const prevIsEnabledRef = useRef<boolean | null>(null);
  const prevDocumentTypeRef = useRef<string | null>(null);

  // Track last observed value to detect user changes vs programmatic changes
  const prevValueRef = useRef<boolean | null | undefined>(null);
  const lastSetByUsRef = useRef(false);
  const lastProgrammaticValueRef = useRef<boolean | null>(null);
  const userOverrodeRef = useRef(false);

  useEffect(() => {
    const meta = getFieldMeta?.(field?.name) || {};
    const currentValue = meta.value; // true/false or undefined
    const initialValue = meta.initialValue;
    const prevIsEnabled = prevIsEnabledRef.current;
    const prevDocumentType = prevDocumentTypeRef.current;

    const input: DeliveryNoteToggleInput = {
      prevIsEnabled,
      isEnabled,
      prevDocumentType,
      documentType: documentType ?? null,
      isEdit,
      initialDocumentType: initialDocumentType ?? null,
      isClone,
      disabledByDefault,
      currentValue,
      initialValue,
      prevValue: prevValueRef.current,
      lastSetByUs: lastSetByUsRef.current,
      lastProgrammaticValue: lastProgrammaticValueRef.current,
      userOverrode: userOverrodeRef.current,
    };

    const result: DeliveryNoteToggleResult = computeDeliveryNoteToggleAction(input);
    // Apply set action if requested by the pure function
    if (typeof result.set !== 'undefined') {
      // mark that we're programmatically setting so follow-up observations can ack it
      lastSetByUsRef.current = result.lastSetByUs;
      lastProgrammaticValueRef.current = result.lastProgrammaticValue;
      userOverrodeRef.current = result.userOverrode;

      setFieldValue?.(field?.name, result.set);
      // Immediately reflect this in prevValueRef so we don't miss-detect changes until Formik updates
      prevValueRef.current = result.set;
      if (result.set) {
        // Trigger side effect to set `shipDate`
      }
    } else {
      // No programmatic set; update flags returned
      lastSetByUsRef.current = result.lastSetByUs;
      lastProgrammaticValueRef.current = result.lastProgrammaticValue;
      userOverrodeRef.current = result.userOverrode;

      // Detect and update prevValueRef and userOverrode if user interacted
      const prevValDefined = prevValueRef.current !== undefined && prevValueRef.current !== null;
      if (prevValDefined && currentValue !== prevValueRef.current) {
        // If this change was NOT caused by our programmatic set, mark userOverrode
        if (!lastSetByUsRef.current) {
          userOverrodeRef.current = true;
        } else {
          // If it was caused by our programmatic set, clear the marker (ack)
          lastSetByUsRef.current = false;
          lastProgrammaticValueRef.current = null;
        }
      }
      prevValueRef.current = currentValue;
    }

    prevIsEnabledRef.current = isEnabled;
    prevDocumentTypeRef.current = documentType ?? null;
  }, [
    isEnabled,
    disabledByDefault,
    isEdit,
    isClone,
    documentType,
    initialDocumentType,
    field?.name,
    setFieldValue,
    getFieldMeta,
  ]);

  const getSection = useCallback(() => {
    if (isEnabled) {
      return (
        <div>
          <BToggle field={field} formik={formik} />
        </div>
      );
    }
  }, [field, formik, isEnabled]);

  return getSection();
};
